import { useMemo } from 'react';
import { ArrowRightLeft, BookMarked, Eye, Receipt } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/lib/cn';
import { formatMoney, parseMoney } from '@/shared/lib/money';
import { useOrdenTabla } from '@/shared/lib/tablaOrden';
import { SortableTh } from '@/shared/components/ui/sortable-th';
import type { MovimientoRow } from '../api';
import { CATEGORIAS_SIN_CUENTA_CONTABLE, puedeReclasificar } from './ReclasificarCuentaDialog';

/**
 * Mirror of Screen_Tesoreria's main gallery. One row per
 * `MovimientoCajas` record, with Crédito (Ingreso) / Débito (Egreso)
 * columns split on `TipoMovimiento_MC`. `Saldo` is server-computed.
 *
 * Anulado rows render faded and struck-through so admins can still see
 * them when the status filter is 'all' or 'Anulado'.
 */
/**
 * Cajas where the operator can manually attach a per-Ingreso comisión
 * Egreso. AR uses the legacy `MercadoPago` caja; MX (Nova) uses
 * `Nova Mercado Pago`. The server applies the same set as the
 * parent-validation predicate — keep them aligned.
 */
const MP_COMISION_CAJAS: ReadonlySet<string> = new Set([
  'MercadoPago',
  'Nova Mercado Pago',
]);

/**
 * Row predicate matching PowerApps `btn_AgregarImpuesto_T` visibility:
 * a small "Agregar comisión" button shows on every active MercadoPago
 * (AR + Nova MX) movement — Ingresos (cobranzas) and Egresos (pagos a
 * proveedor) alike — so the operator can attach the per-transaction MP
 * commission as a linked Egreso (`IDImpuesto_MC` = parent id,
 * `IDOperacion_MC` inherited). Tesorería is pais-scoped, so AR and MX
 * comisión Egresos never appear in the same report despite sharing
 * the same `CuentaContable_MC='MercadoPago'` label.
 */
/**
 * Se puede reimputar cualquier movimiento vivo, incluso los automáticos y los
 * que ya están dentro de un cierre cerrado: reclasificar no mueve plata. El
 * server revalida todo esto y además exige que el par exista en el catálogo.
 */
function canReclasificar(row: MovimientoRow): boolean {
  return puedeReclasificar({
    categoria: row.fields.Categoria_MC,
    status: row.fields.Status_MC,
  });
}

/**
 * ¿Se puede pasar a otra caja? Espejo de `PUT /movimiento/:id/caja`: sólo
 * Ingresos/Egresos cargados a mano, vivos, fuera de cierre, que no sean
 * estructura/automatismo ni cuelguen de otro movimiento. `conHijos` son los
 * ids con impuestos/cargos vivos colgados (se trasladarían mal).
 */
function canTrasladar(row: MovimientoRow, conHijos: ReadonlySet<string>): boolean {
  const f = row.fields;
  const tipo = f.TipoMovimiento_MC ?? '';
  return (
    (f.CreacionManual_MC ?? '') === 'SI' &&
    (tipo === 'Ingreso' || tipo === 'Egreso') &&
    (f.Status_MC ?? '') !== 'Anulado' &&
    !(f.IDCierreCaja_MC ?? '').trim() &&
    !(f.IDImpuesto_MC ?? '').trim() &&
    !CATEGORIAS_SIN_CUENTA_CONTABLE.has((f.Categoria_MC ?? '').trim()) &&
    !conHijos.has(row.id)
  );
}

function canAddImpuesto(row: MovimientoRow): boolean {
  const f = row.fields;
  const tipo = f.TipoMovimiento_MC ?? '';
  return (
    MP_COMISION_CAJAS.has(f.MedioPago_MC ?? '') &&
    // Ingreso = comisión de la cobranza. Egreso = comisión del pago a
    // proveedor: antes se posteaba sola con una tasa fija que no coincide
    // con la que cobra MP, así que ahora se carga a mano acá. Los Egresos
    // valen sólo en la caja AR 'MercadoPago': en Nova Mercado Pago (MX) los
    // Egresos son la comisión automática del cobro Nova, los pagos Inter-Co y
    // las patas del neteo, y colgarles una comisión la dejaría sin fondear.
    (tipo === 'Ingreso' ||
      (tipo === 'Egreso' && (f.MedioPago_MC ?? '') === 'MercadoPago')) &&
    // Una comisión no puede colgar de otra: las filas de comisión/impuesto ya
    // son hijas de un movimiento, así que alcanza con mirar el vínculo.
    !(f.IDImpuesto_MC ?? '').trim() &&
    (f.Status_MC ?? '') !== 'Anulado' &&
    !(f.IDCierreCaja_MC ?? '').trim() &&
    // Exclude the neteo's own Ingreso leg (Caja Diferencia → Nova MP): it is
    // itself a transfer, not a cobranza. Attaching a comisión to it would book
    // an unfounded Caja Diferencia debit. Keep aligned with the BE predicate.
    (f.Categoria_MC ?? '') !== 'Transferencia Caja Diferencia'
  );
}

type ColumnaMov = 'tipo' | 'id' | 'fecha' | 'descripcion' | 'cuenta' | 'credito' | 'debito' | 'saldo';

/** Lo que muestra cada columna, para ordenar por ella. */
function valorColumna(r: MovimientoRow, col: ColumnaMov): unknown {
  const f = r.fields;
  const tipo = f.TipoMovimiento_MC ?? '';
  const isApertura = tipo === 'Apertura de Caja' || f.Categoria_MC === 'Apertura';
  const monto = parseMoney(f.MontoNeto_MC);
  switch (col) {
    case 'tipo':
      return tipo === 'Egreso' ? 'Gasto' : tipo;
    case 'id':
      return Number(r.id);
    case 'fecha':
      return `${f.Fecha_MC ?? ''} ${f.Hora_MC ?? ''}`.trim();
    case 'descripcion':
      return f.Descripcion_MC || f.CatDesc_MC || tipo;
    case 'cuenta':
      return f.CuentaContable_MC;
    case 'credito':
      return !isApertura && tipo === 'Ingreso' ? monto : null;
    case 'debito':
      return tipo === 'Egreso' || tipo === 'Cierre de Caja' ? monto : null;
    case 'saldo':
      return r.saldoAt;
  }
}

export function MovimientosTable({
  rows,
  moneda,
  loading,
  error,
  searchActive,
  onView,
  onAgregarImpuesto,
  onReclasificar,
  onTrasladar,
  onRetry,
}: {
  rows: MovimientoRow[];
  moneda: 'ARS' | 'MXN' | 'USD';
  loading: boolean;
  error: boolean;
  searchActive: boolean;
  onView: (row: MovimientoRow) => void;
  onAgregarImpuesto?: (row: MovimientoRow) => void;
  /** Cambiar la cuenta contable del movimiento. Ausente = sin permiso. */
  onReclasificar?: (row: MovimientoRow) => void;
  /** Pasar un movimiento manual a otra caja. Ausente = sin permiso. */
  onTrasladar?: (row: MovimientoRow) => void;
  onRetry: () => void;
}) {
  const emptyMessage = searchActive
    ? 'Sin coincidencias para los filtros aplicados.'
    : 'Sin movimientos en esta caja para el mes seleccionado.';
  // Sin orden elegido queda el cronológico del server (el del saldo).
  const { ordenadas, orden, alternar } = useOrdenTabla(rows, valorColumna);
  // Movimientos con impuestos/cargos vivos colgados: no se trasladan.
  const conHijos = useMemo(
    () =>
      new Set(
        rows
          .filter((r) => (r.fields.Status_MC ?? '') !== 'Anulado')
          .map((r) => (r.fields.IDImpuesto_MC ?? '').trim())
          .filter(Boolean),
      ),
    [rows],
  );
  const trasladarDe = (r: MovimientoRow) =>
    onTrasladar && canTrasladar(r, conHijos) ? () => onTrasladar(r) : undefined;
  const sortTh = (sortKey: ColumnaMov, label: string, className?: string) => (
    <SortableTh
      label={label}
      sortKey={sortKey}
      orden={orden}
      onSort={alternar}
      className={cn('px-3 py-3 font-semibold', className)}
    />
  );
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card">
      {/* Desktop / tablet table view */}
      <div className="hidden min-h-0 flex-1 overflow-y-auto md:block">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              {sortTh('tipo', 'Tipo', 'w-28')}
              {/* Mismo Id que la columna "Id" del Excel de Cierres de Caja: el
                  número con el que se reimporta y se reclasifica. */}
              {sortTh('id', 'Id', 'w-20')}
              {sortTh('fecha', 'Fecha')}
              {sortTh('descripcion', 'Descripción')}
              {sortTh('cuenta', 'Cuenta contable / Categoría')}
              {sortTh('credito', 'Crédito', 'text-right')}
              {sortTh('debito', 'Débito', 'text-right')}
              {sortTh('saldo', 'Saldo', 'text-right')}
              <Th className="w-24 text-right">Acciones</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <SkeletonRows cols={9} />
            ) : error ? (
              <RowStatus cols={9}>
                <span className="text-destructive">
                  No se pudieron cargar los movimientos.
                </span>
                <Button variant="outline" size="sm" onClick={onRetry}>
                  Reintentar
                </Button>
              </RowStatus>
            ) : ordenadas.length === 0 ? (
              <RowStatus cols={9}>{emptyMessage}</RowStatus>
            ) : (
              ordenadas.map((r) => (
                <MovimientoRowView
                  key={r.id}
                  row={r}
                  moneda={moneda}
                  onView={() => onView(r)}
                  onAgregarImpuesto={
                    onAgregarImpuesto && canAddImpuesto(r)
                      ? () => onAgregarImpuesto(r)
                      : undefined
                  }
                  onReclasificar={
                    onReclasificar && canReclasificar(r)
                      ? () => onReclasificar(r)
                      : undefined
                  }
                  onTrasladar={trasladarDe(r)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile card view */}
      <div className="min-h-0 flex-1 overflow-y-auto md:hidden">
        {loading ? (
          <div className="space-y-2 p-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-xl" />
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center gap-3 p-8 text-center text-sm text-destructive">
            <span>No se pudieron cargar los movimientos.</span>
            <Button variant="outline" size="sm" onClick={onRetry}>
              Reintentar
            </Button>
          </div>
        ) : ordenadas.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            {emptyMessage}
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {ordenadas.map((r) => (
              <MovimientoCard
                key={r.id}
                row={r}
                moneda={moneda}
                onView={() => onView(r)}
                onAgregarImpuesto={
                  onAgregarImpuesto && canAddImpuesto(r)
                    ? () => onAgregarImpuesto(r)
                    : undefined
                }
                onReclasificar={
                  onReclasificar && canReclasificar(r)
                    ? () => onReclasificar(r)
                    : undefined
                }
                onTrasladar={trasladarDe(r)}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function MovimientoCard({
  row,
  moneda,
  onView,
  onAgregarImpuesto,
  onReclasificar,
  onTrasladar,
}: {
  row: MovimientoRow;
  moneda: 'ARS' | 'MXN' | 'USD';
  onView: () => void;
  onAgregarImpuesto?: () => void;
  onReclasificar?: () => void;
  onTrasladar?: () => void;
}) {
  const f = row.fields;
  const tipo = f.TipoMovimiento_MC ?? '';
  const categoria = f.Categoria_MC ?? '';
  const isAnulado = f.Status_MC === 'Anulado';
  const monto = parseMoney(f.MontoNeto_MC) ?? 0;
  const isApertura = tipo === 'Apertura de Caja' || categoria === 'Apertura';
  const isIngreso = !isApertura && tipo === 'Ingreso';
  const isEgreso = tipo === 'Egreso' || tipo === 'Cierre de Caja';
  const showAmount = isIngreso || isEgreso;

  return (
    <li
      className={cn(
        'flex flex-col gap-2 px-4 py-3 transition-colors active:bg-muted/40',
        isAnulado && 'opacity-60',
      )}
      onClick={onView}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onView();
        }
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <TipoBadge tipo={tipo} />
          <CategoriaBadge categoria={categoria} tipo={tipo} />
        </div>
        <span className="whitespace-nowrap text-[11px] text-muted-foreground">
          <span className="tabular-nums">#{row.id}</span> · {f.Fecha_MC || '—'}
          {f.Hora_MC && <> · {f.Hora_MC}</>}
        </span>
      </div>
      <div className={cn('text-sm', isAnulado && 'line-through')}>
        <div className="font-medium text-foreground">
          {f.Descripcion_MC || f.CatDesc_MC || tipo || '—'}
        </div>
        {(f.CuentaContable_MC || f.Cliente_MC) && (
          <div className="mt-0.5 text-xs text-muted-foreground">
            {/* Cuenta contable primero, igual que en la tabla y en el
                reporte; la categoría es el badge de arriba. */}
            {f.CuentaContable_MC}
            {f.CuentaContable_MC && f.Cliente_MC ? ' · ' : ''}
            {f.Cliente_MC}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between gap-3 text-sm">
        <span
          className={cn(
            'font-semibold',
            isIngreso && 'text-emerald-700',
            isEgreso && 'text-rose-700',
            !showAmount && 'text-muted-foreground',
          )}
        >
          {showAmount
            ? `${isEgreso ? '−' : '+'}${formatMoney(monto, moneda)}`
            : '—'}
        </span>
        <div className="flex items-center gap-2">
          {onAgregarImpuesto && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onAgregarImpuesto();
              }}
              title="Cargos MercadoPago"
              aria-label="Cargos MercadoPago"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-amber-300 bg-amber-50 text-amber-700 transition-colors hover:bg-amber-100"
            >
              <Receipt className="h-3.5 w-3.5" aria-hidden />
            </button>
          )}
          {onReclasificar && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onReclasificar();
              }}
              title="Cambiar cuenta contable"
              aria-label="Cambiar cuenta contable"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-sky-300 bg-sky-50 text-sky-700 transition-colors hover:bg-sky-100"
            >
              <BookMarked className="h-3.5 w-3.5" aria-hidden />
            </button>
          )}
          {onTrasladar && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onTrasladar();
              }}
              title="Pasar a otra caja"
              aria-label="Pasar a otra caja"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-violet-300 bg-violet-50 text-violet-700 transition-colors hover:bg-violet-100"
            >
              <ArrowRightLeft className="h-3.5 w-3.5" aria-hidden />
            </button>
          )}
          <span
            className={cn(
              'whitespace-nowrap text-xs text-muted-foreground',
              row.saldoAt < 0 && 'text-rose-700',
            )}
          >
            Saldo:{' '}
            <span className="font-semibold">
              {formatMoney(row.saldoAt, moneda)}
            </span>
          </span>
        </div>
      </div>
    </li>
  );
}

function MovimientoRowView({
  row,
  moneda,
  onView,
  onAgregarImpuesto,
  onReclasificar,
  onTrasladar,
}: {
  row: MovimientoRow;
  moneda: 'ARS' | 'MXN' | 'USD';
  onView: () => void;
  onAgregarImpuesto?: () => void;
  onReclasificar?: () => void;
  onTrasladar?: () => void;
}) {
  const f = row.fields;
  const tipo = f.TipoMovimiento_MC ?? '';
  const categoria = f.Categoria_MC ?? '';
  const isAnulado = f.Status_MC === 'Anulado';
  const monto = parseMoney(f.MontoNeto_MC) ?? 0;
  // Apertura's monto is the opening balance — PowerApps shows it ONLY
  // in the Saldo column (Crédito/Débito stay as "—"). PowerApps gates
  // apertura on Categoria_MC; we accept either the tipo or categoria
  // signal for legacy-row safety. The running saldo `row.saldoAt`
  // already includes that monto at this row's position, so the Saldo
  // cell renders correctly without further special casing.
  const isApertura = tipo === 'Apertura de Caja' || categoria === 'Apertura';
  const isIngreso = !isApertura && tipo === 'Ingreso';
  const isEgreso = tipo === 'Egreso' || tipo === 'Cierre de Caja';

  return (
    <tr
      className={cn(
        'transition-colors hover:bg-muted/30',
        isAnulado && 'bg-muted/20 opacity-60',
      )}
    >
      <Td className="whitespace-nowrap">
        <TipoBadge tipo={tipo} />
      </Td>
      <Td className="whitespace-nowrap tabular-nums text-muted-foreground">{row.id}</Td>
      <Td className="whitespace-nowrap text-muted-foreground">
        <div>{f.Fecha_MC || '—'}</div>
        {f.Hora_MC && (
          <div className="text-[11px] text-muted-foreground/80">{f.Hora_MC}</div>
        )}
      </Td>
      <Td className={cn(isAnulado && 'line-through')}>
        <div className="font-medium text-foreground">
          {f.Descripcion_MC || f.CatDesc_MC || tipo || '—'}
        </div>
        {f.Cliente_MC && (
          <div className="mt-0.5 text-xs text-muted-foreground">{f.Cliente_MC}</div>
        )}
      </Td>
      {/* El PAR junto y en el mismo orden que el reporte de Cierres y que
          Configuración → Cuentas Contables: cuenta arriba, categoría abajo.
          Misma tipografía que el reporte (texto plano, sin badge): la píldora
          competía visualmente con el TipoBadge de la primera columna y hacía
          leer la categoría como el dato principal, que es justo al revés. */}
      <Td className="text-muted-foreground">
        <div>{f.CuentaContable_MC || '—'}</div>
        {f.Categoria_MC && (
          <div className="text-xs opacity-75">{f.Categoria_MC}</div>
        )}
      </Td>
      <Td className={cn('text-right font-semibold', isIngreso && 'text-emerald-700')}>
        {isIngreso ? formatMoney(monto, moneda) : '—'}
      </Td>
      <Td className={cn('text-right font-semibold', isEgreso && 'text-rose-700')}>
        {isEgreso ? formatMoney(monto, moneda) : '—'}
      </Td>
      <Td
        className={cn(
          'whitespace-nowrap text-right font-semibold',
          row.saldoAt < 0 && 'text-rose-700',
        )}
      >
        {formatMoney(row.saldoAt, moneda)}
      </Td>
      <Td className="text-right">
        <div className="flex items-center justify-end gap-1">
          {onAgregarImpuesto && (
            <button
              type="button"
              onClick={onAgregarImpuesto}
              title="Cargos MercadoPago"
              aria-label="Cargos MercadoPago"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-amber-300 bg-amber-50 text-amber-700 transition-colors hover:bg-amber-100"
            >
              <Receipt className="h-4 w-4" aria-hidden />
            </button>
          )}
          {onReclasificar && (
            <button
              type="button"
              onClick={onReclasificar}
              title="Cambiar cuenta contable"
              aria-label="Cambiar cuenta contable"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-sky-300 bg-sky-50 text-sky-700 transition-colors hover:bg-sky-100"
            >
              <BookMarked className="h-4 w-4" aria-hidden />
            </button>
          )}
          {onTrasladar && (
            <button
              type="button"
              onClick={onTrasladar}
              title="Pasar a otra caja"
              aria-label="Pasar a otra caja"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-violet-300 bg-violet-50 text-violet-700 transition-colors hover:bg-violet-100"
            >
              <ArrowRightLeft className="h-4 w-4" aria-hidden />
            </button>
          )}
          <button
            type="button"
            onClick={onView}
            title="Ver detalle"
            aria-label="Ver detalle"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          >
            <Eye className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </Td>
    </tr>
  );
}

/**
 * PowerApps Screen_Tesoreria leads each row with a small colored
 * pill identifying the movement type. Colors mirror that legend:
 *   - Apertura → blue (the month's seed balance)
 *   - Cierre   → slate (the month's closing snapshot)
 *   - Ingreso  → green
 *   - Egreso / Gasto → red
 */
function TipoBadge({ tipo }: { tipo: string }) {
  const t = tipo.trim();
  // PowerApps writes "Egreso" but the legacy gallery rendered it as
  // "GASTO" — keep that label so the display matches the screenshot.
  const label =
    t === 'Egreso'
      ? 'GASTO'
      : t === 'Apertura de Caja'
        ? 'APERTURA'
        : t === 'Cierre de Caja'
          ? 'CIERRE'
          : t.toUpperCase() || '—';
  const cls =
    t === 'Apertura de Caja'
      ? 'bg-sky-100 text-sky-700'
      : t === 'Cierre de Caja'
        ? 'bg-slate-200 text-slate-700'
        : t === 'Ingreso'
          ? 'bg-emerald-100 text-emerald-700'
          : t === 'Egreso'
            ? 'bg-rose-100 text-rose-700'
            : 'bg-secondary text-foreground';
  return (
    <span
      className={cn(
        'inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
        cls,
      )}
    >
      {label}
    </span>
  );
}

function CategoriaBadge({
  categoria,
  tipo,
}: {
  categoria: string;
  tipo: string;
}) {
  if (!categoria && !tipo) return <span>—</span>;
  const label = categoria || tipo;
  const cls =
    categoria === 'Anulación'
      ? 'bg-rose-100 text-rose-700'
      : categoria === 'Impuestos'
        ? 'bg-amber-100 text-amber-700'
        : categoria === 'Transferencia' || categoria === 'Depósito'
          ? 'bg-violet-100 text-violet-700'
          : categoria === 'Cierre' || categoria === 'Apertura'
            ? 'bg-slate-200 text-slate-700'
            : 'bg-secondary text-foreground';
  return (
    <span
      className={cn(
        'inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
        cls,
      )}
    >
      {label}
    </span>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={cn('px-3 py-3 font-semibold', className)}>{children}</th>;
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-3 py-3 align-top', className)}>{children}</td>;
}

function RowStatus({ children, cols }: { children: React.ReactNode; cols: number }) {
  return (
    <tr>
      <td colSpan={cols} className="px-4 py-10 text-center text-sm text-muted-foreground">
        <div className="flex items-center justify-center gap-3">{children}</div>
      </td>
    </tr>
  );
}

function SkeletonRows({ cols }: { cols: number }) {
  return (
    <>
      {Array.from({ length: 6 }).map((_, i) => (
        <tr key={i}>
          {Array.from({ length: cols }).map((__, j) => (
            <td key={j} className="px-3 py-3.5">
              <Skeleton className="h-4 w-full max-w-[120px]" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
